import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BadgeCheck, X, ShoppingBag } from 'lucide-react';
const BUYERS = [{
  name: 'Sofía M.',
  city: 'Buenos Aires'
}, {
  name: 'Lucas R.',
  city: 'Rosario'
}, {
  name: 'Valentina F.',
  city: 'Córdoba'
}, {
  name: 'Mateo G.',
  city: 'Mendoza'
}, {
  name: 'Camila P.',
  city: 'La Plata'
}, {
  name: 'Joaquín L.',
  city: 'Mar del Plata'
}, {
  name: 'Martina B.',
  city: 'Salta'
}, {
  name: 'Bruno A.',
  city: 'Tucumán'
}, {
  name: 'Lucía D.',
  city: 'Neuquén'
}, {
  name: 'Tomás S.',
  city: 'Bahía Blanca'
}, {
  name: 'Agustina V.',
  city: 'San Juan'
}, {
  name: 'Nicolás C.',
  city: 'Santa Fe'
}, {
  name: 'Julieta H.',
  city: 'Resistencia'
}, {
  name: 'Franco M.',
  city: 'Posadas'
}, {
  name: 'Florencia T.',
  city: 'Bariloche'
}, {
  name: 'Santiago O.',
  city: 'San Luis'
}, {
  name: 'Micaela R.',
  city: 'Paraná'
}, {
  name: 'Iván P.',
  city: 'Río Cuarto'
}, {
  name: 'Brenda L.',
  city: 'Comodoro Rivadavia'
}, {
  name: 'Federico N.',
  city: 'Corrientes'
}, {
  name: 'Antonella J.',
  city: 'San Miguel de Tucumán'
}, {
  name: 'Gonzalo K.',
  city: 'Catamarca'
}, {
  name: 'Renata Z.',
  city: 'San Rafael'
}, {
  name: 'Emiliano F.',
  city: 'Quilmes'
}, {
  name: 'Pilar G.',
  city: 'Lanús'
}];
const OFFERS = [{
  label: 'Profesorado Resuelto',
  price: '$19.990'
}, {
  label: 'Kit Completo Élite™',
  price: '$29.990'
}];
type Purchase = {
  buyer: {
    name: string;
    city: string;
  };
  offer: {
    label: string;
    price: string;
  };
  minutesAgo: number;
};
function getRandomPurchase(): Purchase {
  return {
    buyer: BUYERS[Math.floor(Math.random() * BUYERS.length)],
    // Elite shows up a bit more often (60/40) since it's the "más elegida"
    offer: OFFERS[Math.random() < 0.6 ? 1 : 0],
    minutesAgo: Math.floor(Math.random() * 14) + 2 // 2–15 minutes ago
  };
}
export function PurchasePopup() {
  const [purchase, setPurchase] = useState<Purchase | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    let cancelled = false;
    const showOne = () => {
      if (cancelled) return;
      setPurchase(getRandomPurchase());
      // Hide after ~5.5s, then schedule the next one
      timeoutRef.current = setTimeout(() => {
        if (cancelled) return;
        setPurchase(null);
        const nextDelay = 40000 + Math.random() * 20000; // 40–60s
        timeoutRef.current = setTimeout(showOne, nextDelay);
      }, 5500);
    };
    // First popup appears 8s after page load to let the user settle in
    timeoutRef.current = setTimeout(showOne, 8000);
    return () => {
      cancelled = true;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);
  const handleClose = () => setPurchase(null);
  const isElite = purchase?.offer.label.includes('Élite');
  return <AnimatePresence data-id="element-316">
      {purchase && <motion.div initial={{
      x: -100,
      opacity: 0,
      scale: 0.9
    }} animate={{
      x: 0,
      opacity: 1,
      scale: 1
    }} exit={{
      x: -100,
      opacity: 0,
      scale: 0.9
    }} transition={{
      type: 'spring',
      stiffness: 280,
      damping: 24
    }}
    // Top on mobile to avoid colliding with StickyMobileCTA at the bottom; bottom-left on desktop
    className="fixed left-2 right-2 md:right-auto top-12 md:top-auto md:bottom-6 md:left-6 z-40 max-w-xs md:max-w-sm bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden" data-id="element-317">
          <div className="flex items-stretch" data-id="element-318">
            {/* Left icon block */}
            <div className={`flex-shrink-0 w-12 md:w-14 flex items-center justify-center ${isElite ? 'bg-brand-900' : 'bg-brand-100'}`} data-id="element-319">
              <ShoppingBag className={`w-5 h-5 md:w-6 md:h-6 ${isElite ? 'text-accent-400' : 'text-brand-700'}`} data-id="element-320" />
            </div>

            {/* Content */}
            <div className="flex-1 px-3 py-2.5 md:px-4 md:py-3 pr-7 min-w-0" data-id="element-321">
              <div className="flex items-center gap-1 mb-0.5" data-id="element-322">
                <BadgeCheck className="w-3.5 h-3.5 text-green-500 flex-shrink-0" data-id="element-323" />
                <span className="text-[10px] md:text-[11px] font-bold text-green-600 uppercase tracking-wider" data-id="element-324">
                  Compra verificada
                </span>
              </div>
              <p className="text-xs md:text-sm font-bold text-gray-900 leading-tight truncate" data-id="element-325">
                {purchase.buyer.name}{' '}
                <span className="font-medium text-gray-500" data-id="element-326">
                  de {purchase.buyer.city}
                </span>
              </p>
              <p className="text-[11px] md:text-xs text-gray-600 leading-tight mt-0.5 truncate" data-id="element-327">
                compró el{' '}
                <span className="font-bold text-brand-700" data-id="element-328">
                  {purchase.offer.label}
                </span>
              </p>
              <p className="text-[10px] md:text-[11px] text-gray-400 font-medium mt-1" data-id="element-329">
                hace {purchase.minutesAgo} min · {purchase.offer.price} ARS
              </p>
            </div>

            {/* Close button */}
            <button onClick={handleClose} aria-label="Cerrar" className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500 transition-colors" data-id="element-330">
              <X className="w-3 h-3" data-id="element-331" />
            </button>
          </div>

          {/* Progress bar (auto-dismiss indicator) */}
          <motion.div initial={{
        scaleX: 1
      }} animate={{
        scaleX: 0
      }} transition={{
        duration: 5.5,
        ease: 'linear'
      }} style={{
        transformOrigin: 'left'
      }} className={`h-0.5 ${isElite ? 'bg-accent-500' : 'bg-brand-500'}`} data-id="element-332" />
        </motion.div>}
    </AnimatePresence>;
}